import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { LoginPageStyles } from '../styles/LoginPageStyles';
import { useMutation, useQuery } from '@apollo/client';
import { LOGIN, LOGIN_WITH_GOOGLE } from '../graphql/login';
import { GET_ME, updateCacheGetMe } from '../graphql/getMe';
import { Role } from '../__generated__/graphql';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { CommonStyles } from '../styles/CommonStyles';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigateHome } from '../hooks/useNavigateHome';

const Login = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  const { data: me, loading: meLoading } = useQuery(GET_ME);
  const { navigateHome } = useNavigateHome();
  const [login, { loading: loginLoading }] = useMutation(LOGIN, {
    onCompleted: () => {
      navigateHome();
    },
    onError: (error) => {
      setLoginError(error.message);
    },
    refetchQueries: [GET_ME],
  });

  const [loginWithGoogle, { loading: googleLoginLoading }] = useMutation(
    LOGIN_WITH_GOOGLE,
    {
      onCompleted: () => {
        navigateHome();
      },
      onError: (error) => {
        setLoginError(error.message);
      },
      refetchQueries: [GET_ME],
    },
  );

  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (me && !meLoading) {
      navigateHome();
    }
  }, [me, meLoading, navigateHome]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let isValid = true;

    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    } else {
      setEmailError('');
    }

    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else {
      setPasswordError('');
    }

    return isValid;
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoginError('');
    if (validateForm()) {
      try {
        await login({
          variables: { loginInput: { email, password } },
        });
      } catch (e) {
        console.error('Login error:', e);
      }
    }
  };

  if (meLoading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }
  return (
    <Box sx={LoginPageStyles.mainContainer}>
      <Box
        sx={LoginPageStyles.loginContainer}
        component="form"
        onSubmit={handleLogin}
      >
        {/* Logo and Title */}
        <Box sx={LoginPageStyles.logoTitleRow}>
          <Box
            component="img"
            src="https://res.cloudinary.com/dh0eho4vx/image/upload/v1752986194/yin_yang_mpsxtn.png"
            alt="Yin Yang Logo"
            sx={LoginPageStyles.logo}
          />
          <Typography sx={LoginPageStyles.title}>Prep Qi</Typography>
        </Box>

        <Typography sx={LoginPageStyles.subtitle}>
          Welcome back! Sign in to continue your TCM journey
        </Typography>

        {/* Google Login */}
        <Box sx={LoginPageStyles.googleButtonContainer}>
          <GoogleLogin
            onSuccess={(credentialResponse) => {
              if (credentialResponse.credential) {
                loginWithGoogle({
                  variables: { token: credentialResponse.credential },
                });
              }
            }}
            onError={() => {
              setLoginError('Google login failed. Please try again.');
            }}
            text="continue_with"
            theme="outline"
            shape="pill"
            width="100%"
            size="large"
            containerProps={{
              style: {
                height: '48px',
                fontSize: '14px',
                fontWeight: '500',
                scale: '1.2',
              }
            }}
          />
        </Box>

        {/* Divider */}
        <Box sx={LoginPageStyles.dividerContainer}>
          <Divider sx={LoginPageStyles.divider} />
          <Typography sx={LoginPageStyles.dividerText}>or</Typography>
          <Divider sx={LoginPageStyles.divider} />
        </Box>
        {/* Email Field */}
        <TextField
          sx={LoginPageStyles.textField}
          label="Email"
          variant="outlined"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={!!emailError}
          helperText={emailError}
        />

        {/* Password Field */}
        <FormControl sx={LoginPageStyles.textField}>
          <InputLabel htmlFor="outlined-adornment-password">
            Password
          </InputLabel>
          <OutlinedInput
            sx={LoginPageStyles.passwordField}
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            error={!!passwordError}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            onChange={(event) => setPassword(event.target.value)}
          />
          {passwordError && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {passwordError}
            </Typography>
          )}
        </FormControl>

        {/* Login Button */}
        <Button
          sx={LoginPageStyles.button}
          type="submit"
          variant="contained"
          disabled={loginLoading || googleLoginLoading}
        >
          {loginLoading || googleLoginLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Sign In'
          )}
        </Button>

        {/* Error Message */}
        {loginError && (
          <Typography sx={LoginPageStyles.errorText}>{loginError}</Typography>
        )}

        {/* Action Buttons */}
        <Button
          sx={LoginPageStyles.signupButton}
          variant="text"
          onClick={() => navigate('/reset-password')}
        >
          Forgot Password?
        </Button>
        <Button
          sx={LoginPageStyles.signupButton}
          variant="outlined"
          onClick={() => navigate('/signup')}
        >
          Don't have an account? Sign up!
        </Button>
      </Box>
    </Box>
  );
};

export default Login;
